import { useLang } from "../i18n";
import { Icon } from "./Icon";
import { CHECKLISTS } from "../data/checklists";

function readDrills() {
  try { return JSON.parse(localStorage.getItem("ppr-drills") || "[]"); } catch { return []; }
}

export function ReadinessCard({ checked }) {
  const { lang } = useLang();

  const totalItems = CHECKLISTS.reduce((s, c) => s + c.items.length, 0);
  const doneItems = CHECKLISTS.reduce((s, c) => s + (checked?.[c.id]?.length || 0), 0);
  const checkPts = Math.round((doneItems / totalItems) * 40);
  const pushOn = typeof window !== "undefined" && localStorage.getItem("ppr-push") === "1";
  const sirenOn = typeof window !== "undefined" && localStorage.getItem("ppr-siren") !== "0";
  const drills = readDrills();
  const drillPts = Math.min(drills.length, 3) * 10;
  const score = checkPts + (pushOn ? 20 : 0) + (sirenOn ? 10 : 0) + drillPts;

  const level = score >= 70
    ? { label: { es: "Excelente", en: "Excellent" }, cls: "text-green-700 border-green-700 bg-green-50" }
    : score >= 40
      ? { label: { es: "Buena", en: "Good" }, cls: "text-amber-700 border-amber-600 bg-amber-50" }
      : { label: { es: "Inicial", en: "Getting started" }, cls: "text-red-700 border-red-700 bg-red-50" };

  const rows = [
    {
      icon: "Backpack",
      label: { es: "Checklists completadas", en: "Checklists completed" },
      pts: checkPts, max: 40,
      detail: `${doneItems}/${totalItems}`,
    },
    {
      icon: "Bell",
      label: { es: "Avisos push NWS activos", en: "NWS push alerts on" },
      pts: pushOn ? 20 : 0, max: 20,
      detail: pushOn ? "✓" : "—",
    },
    {
      icon: "Volume2",
      label: { es: "Sirena de avisos activa", en: "Alert siren on" },
      pts: sirenOn ? 10 : 0, max: 10,
      detail: sirenOn ? "✓" : "—",
    },
    {
      icon: "Waves",
      label: { es: "Simulacros de tsunami completados", en: "Tsunami drills completed" },
      pts: drillPts, max: 30,
      detail: `${Math.min(drills.length, 3)}/3`,
    },
  ];

  return (
    <section data-testid="readiness-card" className="no-print mb-8 rounded-lg border-2 border-[#1C2B4A] bg-white p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
            {lang === "es" ? "Tu Preparación" : "Your Readiness"}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {lang === "es"
              ? "Puntuación basada en lo que has hecho en esta app (se guarda solo en tu dispositivo)."
              : "Score based on what you've done in this app (stored only on your device)."}
          </p>
        </div>
        <div className={`flex items-center gap-3 rounded-lg border-2 px-5 py-3 ${level.cls}`}>
          <span data-testid="readiness-score" className="font-display text-4xl font-black">{score}</span>
          <div>
            <span data-testid="readiness-level" className="block text-sm font-black uppercase tracking-wider">{level.label[lang]}</span>
            <span className="block text-xs font-semibold opacity-70">/ 100</span>
          </div>
        </div>
      </div>

      <ul data-testid="readiness-breakdown" className="mt-5 grid gap-2 sm:grid-cols-2">
        {rows.map((r) => (
          <li key={r.icon} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-[#F8FAFC] px-4 py-2.5">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Icon name={r.icon} className="w-4 h-4 text-[#1C2B4A]" />
              {r.label[lang]}
            </span>
            <span className="text-sm font-bold text-slate-900">{r.detail} <span className="font-normal text-slate-400">({r.pts}/{r.max})</span></span>
          </li>
        ))}
      </ul>

      <div className="mt-5">
        <p className="text-sm font-bold text-slate-700">
          {lang === "es" ? "Historial de simulacros" : "Drill history"}
        </p>
        {drills.length === 0 ? (
          <p data-testid="drill-history-empty" className="mt-1 text-sm text-slate-500">
            {lang === "es"
              ? "Aún no completas ninguno. Corre el Simulacro de tsunami en la pestaña Clima para practicar en familia."
              : "None completed yet. Run the tsunami Drill in the Weather tab to practice as a family."}
          </p>
        ) : (
          <ul data-testid="drill-history" className="mt-2 space-y-1">
            {drills.slice(-5).reverse().map((d, i) => (
              <li key={d.ts} data-testid={`drill-entry-${i}`} className="flex items-center gap-2 text-sm text-slate-600">
                <Icon name="CheckCircle2" className="w-4 h-4 text-green-700" />
                {lang === "es" ? "Simulacro de tsunami" : "Tsunami drill"} — {new Date(d.ts).toLocaleString(lang === "es" ? "es-PR" : "en-US", { dateStyle: "medium", timeStyle: "short" })}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
